import type { kanbanCopy } from '../pt/kanban';

/**
 * English — módulo Kanban.
 *
 * Vocabulário Kanban/Scrum do inglês: board, swimlane, WIP limit, cycle time,
 * throughput, cumulative flow, burndown. Os nomes de métrica já nascem em
 * inglês e ficam iguais nos três idiomas, porque é assim que aparecem na
 * literatura que o usuário lê fora do produto.
 *
 * Abreviação de unidade segue o inglês: `/wk` no lugar de `/sem`, porque o
 * rótulo vive dentro de um card de KPI onde a forma por extenso não cabe.
 */
const kanban: kanbanCopy = {
  module: {
    title: 'Kanban',
    taskSingular: 'task',
    taskPlural: 'tasks',
    emptyTitle: 'No tasks in this project',
    emptyBody: 'Add tasks in the Gantt or create cards directly in the Kanban columns.',
    moveError: 'Could not save the move. Please try again.',
    reorderError: 'Could not reorder. Please try again.',

    actions: {
      publish: 'Publish',
      metrics: 'Metrics',
      sprints: 'Sprints',
      labels: 'Labels',
      columns: 'Columns',
    },

    filters: {
      sprintPlaceholder: 'Sprint',
      sprintAll: 'All',
      sprintBacklog: 'Backlog (no sprint)',
      assigneePlaceholder: 'Assignee',
      assigneeAll: 'All',
      priorityPlaceholder: 'Priority',
      priorityAll: 'All',
      groupPlaceholder: 'Group by',
      /* Tira do celular quando o projeto esta agrupado: o modo raia nao
       * cabe no telefone, e sumir sem dizer nada deixaria a pessoa achando
       * que o agrupamento se perdeu. */
      semRaiaNoCelular: 'Grouped by {{grupo}} on desktop. Here the board shows everything in columns.',
      desagrupar: 'Ungroup',
      sheetTitle: 'Filters',
      sheetGroupFilters: 'Filter by',
      sheetGroupView: 'View',
      sheetClear: 'Clear',
      sheetApply: 'Show {{count}} tasks',
      sheetApplyOne: 'Show 1 task',
    },

    priority: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      none: 'None',
    },

    groupBy: {
      none: 'No grouping',
      sprint: 'By Sprint',
      assignee: 'By Assignee',
      priority: 'By Priority',
    },

    swimlane: {
      sprintActive: 'Active',
      sprintCompleted: 'Completed',
      sprintPlanning: 'Planning',
      backlog: 'Backlog',
      backlogSublabel: 'No sprint',
      unassigned: 'Unassigned',
    },
  },

  card: {
    priority: {
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
    },
    priorityTitle: 'Priority: {{level}}',
    milestone: 'Milestone',
    blocked: 'Blocked',
    overdue: 'Overdue',
    dueSoon: 'Due soon',
    dueDate: 'Due date',
    dependencies: '{{count}} dependency(ies)',
    attachments: '{{count}} attachment(s)',
    comments: '{{count}} comment(s)',
    checklistBadge: 'Checklist: {{done}} of {{total}}',
  },

  column: {
    dropHint: 'Drag cards here',
    newCardPlaceholder: 'Task name...',
    addCard: 'Add task',
    add: 'Add',
    cancel: 'Cancel',
    useTemplate: 'Create from template',
    addColumn: 'New column',
    newColumnPlaceholder: 'Column name...',
    newColumnCategory: 'Will be {{categoria}}',
  },

  swimlane: {
    empty: 'Empty',
    newCardPlaceholder: 'Task name...',
    add: 'Add',
    cancel: 'Cancel',
    addCard: 'Card',
  },

  categories: {
    not_started: 'To do',
    in_progress: 'In progress',
    on_hold: 'On hold',
    completed: 'Done',
    hint: {
      not_started: 'Resets progress. The Portfolio counts it as not started.',
      in_progress: 'Sets progress to 5% when the card is at zero.',
      on_hold: 'Leaves progress untouched. This is where "Paused" and "Blocked" live.',
      completed: 'Closes progress at 100%. This is what the Portfolio counts as delivered.',
    },
  },

  columnSettings: {
    title: 'Board columns',
    subtitle: 'The column says where the card is. The category says what it means in the Portfolio.',
    newColumnName: 'New column',
    namePlaceholder: 'Column name',
    category: 'Category',
    addColumn: 'Add column',
    cancel: 'Cancel',
    save: 'Save',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Delete column',
    deleteTitle: 'Delete the "{{name}}" column',
    deleteWithCards: {
      one: 'It holds {{count}} card. Choose where it goes.',
      other: 'It holds {{count}} cards. Choose where they go.',
    },
    deleteEmpty: 'The column is empty.',
    deleteTarget: 'Move the cards to',
    deleteConfirm: 'Delete column',
    needsDone: 'The board needs at least one column in the Done category.',
    needsTodo: 'The board needs at least one column in the To do category.',
    needsName: 'Every column needs a name.',
    saveError: 'Could not save the columns. Please try again.',
    cardCount: {
      zero: 'No cards in this column',
      one: '{{count}} card in this column',
      other: '{{count}} cards in this column',
    },
  },

  labelSettings: {
    title: 'Manage Labels',
    empty: 'No labels created.',
    newLabelName: 'New Label',
    namePlaceholder: 'Label name',
    addLabel: 'Add Label',
    cancel: 'Cancel',
    save: 'Save',
  },

  metrics: {
    title: 'Kanban Metrics',

    tabs: {
      overview: 'Overview',
      cycletime: 'Cycle Time',
      flow: 'Cumulative Flow',
      throughput: 'Throughput',
      burndown: 'Burndown',
    },

    periods: {
      d7: '7 days',
      d14: '14 days',
      d30: '30 days',
    },

    avgCycleTime: 'Average Cycle Time',
    medianCycleTime: 'Median Cycle Time',
    completedTasks: 'Completed Tasks',
    lastDays: 'in the last {{days}} days',
    avgThroughput: 'Average Throughput',
    perWeek: '{{value}}/wk',
    tasksPerWeek: 'tasks per week',
    emptyOverview: 'Not enough history yet. Move tasks between columns to generate metrics.',

    axisDays: 'Days',
    tooltipDays: '{{value}} days',
    tooltipCycleTime: 'Cycle Time',
    emptyCycleTime: 'No cycle time data. Move tasks to the final column to generate data.',

    emptyFlow: 'No cumulative flow data.',

    axisWeekOf: 'Week of',
    tooltipTasks: '{{value}} tasks',
    seriesCompleted: 'Completed',
    emptyThroughput: 'No throughput data. Complete tasks to generate data.',

    sprintLabel: 'Sprint:',
    seriesIdeal: 'Ideal',
    seriesRemaining: 'Remaining',
    emptyBurndown: 'No burndown data for the active sprint.',
  },

  sprints: {
    title: 'Manage Sprints',
    empty: 'No sprints yet. Create your first sprint to organize the tasks.',

    status: {
      planning: 'Planning',
      active: 'Active',
      completed: 'Completed',
      canceled: 'Canceled',
    },

    namePlaceholder: 'Sprint name',
    goalPlaceholder: 'Sprint goal',
    goalPlaceholderNew: 'Sprint goal (optional)',
    startLabel: 'Start',
    endLabel: 'End',
    startPlaceholder: 'Start',
    endPlaceholder: 'End',
    start: 'Start Sprint',
    complete: 'Complete Sprint',
    delete: 'Delete Sprint',
    deleteConfirm: 'Delete this sprint? Its tasks go back to the backlog.',
    create: 'Create',
    newSprint: 'New Sprint',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
  },

  detail: {
    overEstimate: 'over estimate',
    groupExecution: 'Execution',
    groupSchedule: 'Schedule',
    groupEffort: 'Effort',
    useTemplate: 'Use template',
    predecessorsAdd: 'Add',
    templates: {
      user_story: {
        label: 'User Story',
        html: '<h2>User Story</h2><p><strong>As a</strong> [type of user],</p><p><strong>I want</strong> [action/feature],</p><p><strong>So that</strong> [benefit/value].</p><h2>Acceptance Criteria</h2><ul><li>[ ] </li><li>[ ] </li></ul><h2>Notes</h2><p></p>',
      },
      bug_report: {
        label: 'Bug Report',
        html: '<h2>Bug Description</h2><p></p><h2>Steps to Reproduce</h2><ol><li></li><li></li><li></li></ol><h2>Expected Behavior</h2><p></p><h2>Actual Behavior</h2><p></p><h2>Environment</h2><ul><li>Browser: </li><li>System: </li></ul>',
      },
      spike: {
        label: 'Spike / Research',
        html: '<h2>Goal</h2><p></p><h2>Questions to Answer</h2><ul><li></li><li></li></ul><h2>Constraints</h2><p>Timebox: </p><h2>Expected Outcome</h2><p></p>',
      },
      task: {
        label: 'Technical Task',
        html: '<h2>Context</h2><p></p><h2>What to do</h2><ul><li></li></ul><h2>Definition of Done</h2><ul><li>[ ] Code implemented</li><li>[ ] Tests passing</li><li>[ ] Code review approved</li></ul>',
      },
    },

    priority: {
      none: 'None',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
    },

    undoField: 'Change {{field}}',

    namePlaceholder: 'Task name',
    prevTask: 'Previous task (←)',
    nextTask: 'Next task (→)',
    saving: 'Saving...',
    saved: 'Saved',

    description: 'Description',
    templateLabel: 'Template:',
    templateReplaceTitle: 'Replace description?',
    templateReplaceMessage: 'The current description will be replaced by the template. You can undo with Ctrl+Z in the editor.',
    templateReplaceConfirm: 'Replace',

    templateApplyTitle: 'Apply template',
    templateApplyMessage: 'The current description and checklist will be replaced by the template content. Priority, estimate and labels are applied too.',
    templateApplyConfirm: 'Apply',
    saveAsTemplate: 'Save as template',
    templateSaveTitle: 'Save as template',
    templateSaveNamePlaceholder: 'Template name',
    templateSaveHint: 'Includes description, checklist, labels, priority and estimate. Assignees and dates are left out.',
    templateSaveCancel: 'Cancel',
    templateSaveConfirm: 'Save',
    templateSaved: 'Template saved',
    templateSaveError: 'Could not save the template',
    cardActions: 'Card actions',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    linkCopyError: 'Could not copy the link',
    duplicateCard: 'Duplicate card',
    deleteCard: 'Delete card',
    duplicated: 'Card duplicated',
    deleteConfirmTitle: 'Delete card',
    deleteConfirmMessage: 'Delete this task? Subtasks will also be deleted. This action cannot be undone.',
    deleteConfirmLabel: 'Delete',
    descriptionPlaceholder: 'Describe the task, user story, acceptance criteria... Use @ to mention members',

    attachments: {
      title: 'Attachments',
      empty: 'No attachments. Drag a file here or paste a link.',
      addFile: 'Attach file',
      addLink: 'Add link',
      linkPlaceholder: 'https://...',
      linkInvalid: 'Invalid address. Use http, https or mailto.',
      dropHere: 'Drop to attach',
      uploading: 'Uploading...',
      download: 'Download',
      open: 'Open',
      remove: 'Remove',
      removeConfirm: 'Remove this attachment?',
      by: 'by {{name}}',
      mimeNotAllowed: 'File type not accepted. Upload PDF, Word, Excel, PowerPoint, text, image or ZIP.',
      quota: {
        usage: '{{used}} of {{limit}} in attached files',
        nearLimit: '{{left}} of attachment space left.',
        upgradeHint: '{{plan}} gives you {{planLimit}}.',
        seePlans: 'See plans',
        blockedTitle: 'Attachment space is full',
        blockedBody: 'You have used {{used}} of the {{limit}} on the {{currentPlan}} plan. Free up space by removing old attachments, or change plan.',
        fileTooLargeTitle: 'File too large',
        fileTooLargeBody: 'This file is {{size}} and the per-file limit on the {{currentPlan}} plan is {{limit}}. Compress the file, or change plan.',
        close: 'Close',
      },
    },

    checklist: 'Checklist',
    checklistPlaceholder: 'Add item...',
    checklistAdd: 'Add',

    subtasks: 'Subtasks',
    subtaskAdd: 'Add',
    subtasksEmpty: 'No subtasks. Subtasks show up in the Gantt as a hierarchy.',
    newSubtask: 'New subtask',

    status: 'Status',
    statusPlaceholder: 'Select',
    autoBadge: 'Auto',

    sprint: 'Sprint',
    backlog: 'Backlog',

    startLabel: 'Start',
    startPlaceholder: 'Select start',
    endLabel: 'End',
    endPlaceholder: 'Select end',
    durationLabel: 'Duration',
    durationPlaceholder: 'e.g. 5d, 16h, 2w',
    durationError: 'Format: 5d, 16h, 2w, 1m',

    progress: 'Progress',
    progressAria: 'Task progress',

    estimated: 'Estimated',
    actual: 'Actual',
    hoursPlaceholder: '0h',

    predecessors: 'Predecessors',
    predecessorsEmpty: 'None',
    edit: 'Edit',

    assignees: 'Assignees',
    priorityLabel: 'Priority',
    labels: 'Labels',

    blocked: 'Blocked',
    blockedAria: 'Task blocked',
    yes: 'Yes',
    no: 'No',
  },
};

export default kanban;
